/**
 * IXVerify v2 — result screens (ixv2 WP-3.2; plan §3.2 step 5 + mismatch path).
 *
 * Success: full-bleed green — "Published · sent to CRIO + you", verification
 * code, share/view-PDF, request second check.
 * Mismatch: full-bleed red — the UI celebrates the catch ("IXVerify just
 * prevented a wrong-kit dispense"): a mismatch record is the tool WORKING.
 * Color never carries the meaning alone (icon + words on both).
 */
import { CheckCircle2, ExternalLink, FileCheck2, Home, Plus, Share2, ShieldAlert, UserCheck } from 'lucide-react';
import type { PublishResult } from '../../lib/api';

const lightBtn =
  'inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-white/15 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/60 motion-reduce:transition-none';

export function SuccessScreen({
  result,
  onViewReport,
  onShare,
  canShare,
  onRequestSecondCheck,
  onNewDispense,
  onDone,
}: {
  result: PublishResult;
  onViewReport: () => void;
  onShare: () => void;
  canShare: boolean;
  onRequestSecondCheck: () => void;
  onNewDispense: () => void;
  onDone: () => void;
}) {
  const sentTo = result.distribution || [];
  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto bg-green-600 px-6 text-center text-white"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top) + 2rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)',
      }}
      data-testid="success-screen"
    >
      <CheckCircle2 className="h-16 w-16" aria-hidden="true" />
      <h1 className="mt-4 text-2xl font-bold">Verified &amp; published</h1>
      <p className="mt-1 text-lg font-semibold tabular-nums">{result.report.verification_code}</p>
      <p className="mt-2 max-w-xs text-sm text-white/90">
        {sentTo.length > 0
          ? `Report on its way to ${sentTo.length} recipient${sentTo.length === 1 ? '' : 's'}.`
          : 'Report sealed and stored.'}
      </p>

      <div className="mt-6 flex w-full max-w-xs flex-col gap-2">
        <button type="button" onClick={onViewReport} className={lightBtn}>
          <ExternalLink className="h-4 w-4" aria-hidden="true" /> View report PDF
        </button>
        {canShare && (
          <button type="button" onClick={onShare} className={lightBtn}>
            <Share2 className="h-4 w-4" aria-hidden="true" /> Share
          </button>
        )}
        <button type="button" onClick={onRequestSecondCheck} className={lightBtn} data-testid="request-second-check">
          <UserCheck className="h-4 w-4" aria-hidden="true" /> Request second check
        </button>
      </div>

      <div className="mt-8 flex w-full max-w-xs flex-col gap-2">
        <button
          type="button"
          onClick={onNewDispense}
          className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-green-700 transition-colors duration-150 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-white/60 motion-reduce:transition-none"
        >
          <Plus className="h-4 w-4" aria-hidden="true" /> New dispense
        </button>
        <button type="button" onClick={onDone} className={lightBtn}>
          <Home className="h-4 w-4" aria-hidden="true" /> Done
        </button>
      </div>
    </div>
  );
}

export function MismatchRecordedScreen({
  onDone,
}: {
  onDone: () => void;
}) {
  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto bg-red-600 px-6 text-center text-white"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top) + 2rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)',
      }}
      data-testid="mismatch-screen"
    >
      <ShieldAlert className="h-16 w-16" aria-hidden="true" />
      <h1 className="mt-4 text-2xl font-bold">Mismatch recorded</h1>
      <p className="mt-2 max-w-xs text-sm text-white/90">
        IXVerify just prevented a wrong-kit dispense. Site admins have been notified and a mismatch report was filed —
        this session can never publish a dispense-verified record.
      </p>
      <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-white">
        <FileCheck2 className="h-4 w-4" aria-hidden="true" /> That's the tool working. Good catch.
      </p>
      <div className="mt-8 w-full max-w-xs">
        <button
          type="button"
          onClick={onDone}
          className="inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-red-700 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-white/60 motion-reduce:transition-none"
        >
          <Home className="h-4 w-4" aria-hidden="true" /> Back to dashboard
        </button>
      </div>
    </div>
  );
}
